import type { OfferStatus } from "@/features/offer/types/offer";

export const ChatStepId = {
  ActiveLoan: "active_loan",
  KycFailed: "kyc_failed",
  Cnp: "cnp",
  BcConsent: "bc_consent",
  Email: "email",
  Income: "income",
  BcScoring: "bc_scoring",
  PreliminaryOffer: "preliminary_offer",
  Kyc: "kyc",
  Terms: "terms",
  AnafConsent: "anaf_consent",
  Questionnaire: "questionnaire",
  ProofOfIncome: "proof_of_income",
  OfferRenewal: "offer_renewal",
  OfferRequest: "offer_request",
  Application: "application",
} as const;

export type ChatStepId = (typeof ChatStepId)[keyof typeof ChatStepId];

// Everything the engine decides on, read from the backend on every visit.
// Nothing about the user's position is stored on the client.
export interface OnboardingFacts {
  hasActiveLoan: boolean;
  isCnpCompleted: boolean;
  // Email saved, the code may still be missing.
  isEmailSaved: boolean;
  isEmailVerified: boolean;
  // BC and ANAF both accepted
  isTermsAccepted: boolean;
  isBcTermsAccepted: boolean;
  hasDeclaredIncome: boolean;
  isScoringDone: boolean;
  hasBcScore: boolean;
  // BC has no phone to verify the CNP by SMS: scored after KYC instead.
  isBcPhoneMissing: boolean;
  isKycValid: boolean;
  // KYC rejected; only support can validate it.
  isKycFailed: boolean;
  isQuestionnaireCompleted: boolean;
  isProofOfIncomeProcessing: boolean;
  hasFinalOffer: boolean;
  offerRequestStatus: OfferStatus | null;
  // The last offer request ended (offer or none) and its offers expired: a
  // returning user is checked again.
  isOfferExpired: boolean;
  hasAvailableOffer: boolean;
}

// What the user already told us, shown back as their answers in the thread.
export interface ChatProfile {
  email: string | null;
  cnp: string | null;
  declaredIncome: number | null;
}

export interface ChatStepCopy {
  // Bot messages asking for the step, one paragraph each.
  prompt: (facts: OnboardingFacts) => string[];
  cta: (facts: OnboardingFacts) => string;
  // The user's side once the step is done; steps without it leave no trace.
  done?: string;
  answer?: (profile: ChatProfile) => string | null;
}

export interface ChatStep {
  id: ChatStepId;
  // Steps that must be done (or not apply) first; a scenario must list them earlier.
  needs: readonly ChatStepId[];
  // Gate for steps that only exist in some states (active loan, proof of income).
  appliesWhen?: (facts: OnboardingFacts) => boolean;
  isDone: (facts: OnboardingFacts) => boolean;
  screen: {
    // Where the CTA opens the step.
    entry: (facts: OnboardingFacts) => string;
    // Path prefixes the user may move through while this step is current.
    group: readonly string[];
  };
  copy: ChatStepCopy;
}

export interface ChatScenario {
  id: string;
  version: number;
  steps: readonly ChatStepId[];
}

// A step answered inside the chat instead of on its own screen. It saves
// through the existing APIs; the refreshed facts then move the chat on.
export interface ChatTurnProps {
  scenario: ChatScenario;
  step: ChatStep;
  // The step started during this visit, so its question is typed out.
  isPromptTyped: boolean;
  facts: OnboardingFacts;
  profile: ChatProfile;
  onAnswered: () => void;
}
